import omit from 'lodash/omit';
import { Dispatch } from 'redux';
import { clearFields } from 'redux-form';
import {
    CannedQuestionName,
    Question,
    questionCustomisationFilterPrefix,
    QuestionCustomisationFilterType,
} from '../../api/models/survey';
import { COPY_BILLING_CONTACT_INFO_FIELD } from '../../containers/checkout/constants';
import {
    FormAnswer,
    FormData,
    isInventoryQuestion,
    isTaxQuestion,
    SurveyQuestion,
} from '../../models/survey';

const hasRequiredChildOrParentOrSibling = ({
    questions,
    currentQuestionName,
    currentQuestionParentName,
}: {
    questions: any[];
    currentQuestionName: string;
    currentQuestionParentName: string | undefined;
}) =>
    questions.some(
        (question) =>
            (question.name === currentQuestionParentName ||
                question.renderConditionTrigger?.parentQuestionId ===
                    currentQuestionName ||
                (currentQuestionParentName
                    ? question.renderConditionTrigger?.parentQuestionId ===
                      currentQuestionParentName
                    : false)) &&
            question.required,
    );

export const isRequiredQuestion = (
    question: SurveyQuestion,
    questions: SurveyQuestion[],
) =>
    question.required ||
    isInventoryQuestion(question.choices) ||
    isTaxQuestion(question.name as CannedQuestionName) ||
    hasRequiredChildOrParentOrSibling({
        questions,
        currentQuestionName: question.name,
        currentQuestionParentName:
            question.renderConditionTrigger?.parentQuestionId,
    });

export const clearSurveyField =
    (formName: string, field: string) => (dispatch: Dispatch<any>) =>
        dispatch(clearFields(formName, true, true, field));

/**
 * Attendee IDs in LOS are numbers prefixed with `A-` while in NOS are integer numbers
 */
export const isAttendeeField = (field: string): boolean => {
    return field.indexOf('A-') === 0 || /^[0-9]+$/.test(field);
};

export const removeCopyBillingInfoField = (formData: FormData) =>
    Object.keys(formData).reduce((otherFields, fieldName: string) => {
        return {
            ...otherFields,
            [fieldName]: omit(
                formData[fieldName],
                COPY_BILLING_CONTACT_INFO_FIELD,
            ),
        };
    }, {});

export const isCannedQuestion = (question: Pick<SurveyQuestion, 'name'>) =>
    question.name.startsWith(
        questionCustomisationFilterPrefix[
            QuestionCustomisationFilterType.canned
        ],
    );

export const isCustomQuestion = (question: Pick<SurveyQuestion, 'name'>) =>
    question.name.startsWith(
        questionCustomisationFilterPrefix[
            QuestionCustomisationFilterType.custom
        ],
    );

export const areMultipleResponsesSelected = (response: string) =>
    !!response?.includes('|');

export const getMultipleChoicesSelected = (
    question: Question,
    response: string,
) => {
    const existingChoices = Object.fromEntries(
        question.choices.map((choice) => [
            choice.answer.text.toString(),
            choice,
        ]),
    );
    return response.split('|').flatMap((choiceSelected) => {
        const choice = existingChoices[choiceSelected.trim()];
        return choice ? [choice.id] : [];
    });
};

export const transformAddressFromApi = (formAddress: Record<string, string>) =>
    ({
        address1: formAddress.address_1,
        address2: formAddress.address_2,
        city: formAddress.city,
        country: formAddress.country,
        postal: formAddress.postal_code,
        region: formAddress.region,
    } as Record<string, string>);

export const getChoiceIdByText = (
    question: Question,
    questionName: string,
    responses: Record<string, FormAnswer>,
) =>
    question.choices.find(
        (choice) => choice.answer.text.toString() === responses[questionName],
    )?.id;
